/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';

import type { CancelDeflectionBodyProps } from 'bundles/my-purchases-graphql/components/modals/CancelDeflectionModal';
import {
  isCplusAnnualPromoCode,
  isCplusV1PromoCode,
} from 'bundles/my-purchases-graphql/components/modals/cancelDeflection/constants';
import TrackedButton from 'bundles/page/components/TrackedButton';

import certificateStackFullBlue from 'images/my-purchases-graphql/certificate-stack-full_blue.png';
import toolsSkillsAbilitiesBlue from 'images/my-purchases-graphql/tools-skills-abilities_blue.png';

import _t from 'i18n!nls/my-purchases-graphql';

const styles = {
  content: () => css`
    margin: 0 20px;
    display: flex;
    ${breakpoints.down('xs')} {
      display: block;
      margin: 0 10px;
    }
  `,
  left: () => css`
    width: 370px;
    background-color: white;
  `,
  bigBlue: () => css`
    font-size: 44px;
    line-height: 56px;
    font-weight: 600;
    color: var(--cds-color-blue-700);
    margin-top: 4px;
    margin-bottom: 25px;
  `,
  message: () => css`
    margin-bottom: 32px;
  `,
  right: () => css`
    padding-left: 70px;
    padding-top: 20px;
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  emphasis: () => css`
    font-weight: 700;
  `,
  accept: () => css`
    margin-right: 20px;
  `,
  buttonContainer: () => css`
    display: flex;
    margin-bottom: 20px;
  `,
};

const CplusDeflection = (props: CancelDeflectionBodyProps) => {
  const cPlusMonthlyPercentagePromoCopy = {
    topLine: _t('Keep learning with Coursera Plus'),
    bigLine: _t('Get 50% OFF'),
    message: (
      <FormattedMessage
        message={_t('Continue to have {accessToLearning} and {save50Percent}!')}
        accessToLearning={<span css={styles.emphasis}>{_t('access to unlimited learning')}</span>}
        save50Percent={<span css={styles.emphasis}>{_t('save 50% on your next bill')}</span>}
      />
    ),
    accept: _t('Accept offer'),
    reject: _t('Skip offer'),
  };
  const cPlusMonthlyOneMonthPromoCopy = {
    ...cPlusMonthlyPercentagePromoCopy,
    bigLine: _t('Get 1 month free'),
    message: (
      <FormattedMessage
        message={_t('Continue to have {accessToLearning} and {nextMonthOnUs}!')}
        accessToLearning={<span css={styles.emphasis}>{_t('access to unlimited learning')}</span>}
        nextMonthOnUs={<span css={styles.emphasis}>{_t('next month is on us')}</span>}
      />
    ),
  };
  const cPlusAnnualPromoCopy = {
    topLine: _t('Keep learning with Coursera Plus'),
    bigLine: _t('Get 25% OFF'),
    message: (
      <FormattedMessage
        message={_t('Continue to have {accessToLearning} and {save25Percent}!')}
        accessToLearning={<span css={styles.emphasis}>{_t('access to unlimited learning')}</span>}
        save25Percent={<span css={styles.emphasis}>{_t('save 25% on your next bill')}</span>}
      />
    ),
    accept: _t('Accept offer'),
    reject: _t('Skip offer'),
  };

  const { handleApplyDiscount, handleCancelSubscription, didCancelOrApplyDiscount, promoCode } = props;
  let copies = cPlusMonthlyOneMonthPromoCopy;
  if (isCplusV1PromoCode(promoCode)) {
    if (isCplusAnnualPromoCode(promoCode)) {
      copies = cPlusAnnualPromoCopy;
    } else {
      copies = cPlusMonthlyPercentagePromoCopy;
    }
  }
  const decorationPicUrl = isCplusV1PromoCode(promoCode) ? toolsSkillsAbilitiesBlue : certificateStackFullBlue;
  return (
    <div css={styles.content}>
      <div css={styles.left}>
        <Typography2 component="h3" variant="subtitleMedium">
          {copies.topLine}
        </Typography2>
        <div css={styles.bigBlue}>{copies.bigLine}</div>
        <div css={styles.message}>{copies.message}</div>
        <div css={styles.buttonContainer}>
          <span css={styles.accept}>
            <Button
              variant="primary"
              component={TrackedButton}
              onClick={handleApplyDiscount}
              trackingName="apply_discount"
              withVisibilityTracking={true}
              requireFullyVisible={false}
              data-testid="apply_discount"
              disabled={didCancelOrApplyDiscount}
            >
              {copies.accept}
            </Button>
          </span>
          <Button
            variant="secondary"
            component={TrackedButton}
            onClick={handleCancelSubscription}
            trackingName="cancel_subscription"
            data-testid="cancel_subscription"
            withVisibilityTracking={true}
            requireFullyVisible={false}
            disabled={didCancelOrApplyDiscount}
          >
            {copies.reject}
          </Button>
        </div>
      </div>
      <div css={styles.right}>
        <Imgix src={decorationPicUrl} alt={Imgix.DECORATIVE} maxWidth={150} maxHeight={150} />
      </div>
    </div>
  );
};

export default CplusDeflection;
